import Config from 'config/settings';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PATH_ORBIT,
	BEHAVIOUR_PROGRESS_PARALLAX,
	BEHAVIOUR_REPELL,
	PHYS_LERPING,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

//Script group for the per-lane counters (ParticleCounter closures). Cancelled
//at every phase transition: every LIVE spaces phase's arm pass runs
//deactivateAllBehaviours on the lanes it reuses (killing their counters in
//the reference); lanes a new phase merely hide()s keep their counters alive
//there, but every one of those pending triggers only writes invisible state
//(alpha-0 fades, off-screen lerps) — cancelling them wholesale is
//output-identical and is the one intended simplification:
export const GROUP_SPACES = 'spaces';

/** The _getFrameSizes reuse-object shape (reference quick-wins pattern). */
export interface FrameSizes {
	x1: number;
	x2: number;
	width: number;
	xc: number;
	y1: number;
	y2: number;
	height: number;
	yc: number;
}

/**
 * SpacesContext — the shared plumbing every spaces phase file works
 * through: the pool slice (with the reference's mobile cap), the lane
 * helpers that port HomeParticle/SpacesParticleProcessor's shared moves
 * (deactivateAllBehaviours / _reset / _spacesRepellStart / hide /
 * lerpToPosition / setEase), the _getFrameSizes port and the
 * physics-onTargetReached handler registry (the reference's per-particle
 * ParticlePhysics.onTargetReached closures become per-lane handler ids
 * that phases claim at init and the department dispatches).
 */
export class SpacesContext {
	public readonly core: ParticleCore;
	public readonly range: PoolRange;
	public readonly deps: DepartmentDeps;

	//Mobile pool cap (reference: pool sliced to spacesParticlesMobile):
	public readonly activeCount: number;

	//Per-lane physics-handler ids (0 = none), indexed lane - range.start:
	public readonly physHandler: Uint8Array;
	private _physicsHandlers: Array<(lane: number) => void> = [];

	constructor(core: ParticleCore, deps: DepartmentDeps) {
		this.core = core;
		this.deps = deps;
		this.range = core.pool('spaces');

		this.activeCount =
			window.innerWidth < Config.mobileBreakpoint
				? Math.min(HomeFieldConfig.spacesParticlesMobile, this.range.count)
				: this.range.count;

		this.physHandler = new Uint8Array(this.range.count);
	}

	//Physics onTargetReached registry
	//-----------------------------------------------------------------------------------------------

	/** A phase claims a handler id at init(); arm passes write it into
	 *  physHandler lanes (ParticlePhysics.onTargetReached = closure). */
	public registerPhysicsHandler(handler: (lane: number) => void): number {
		this._physicsHandlers.push(handler);
		return this._physicsHandlers.length; //ids are 1-based (0 = none)
	}

	/** The kernel's lerp-completion routing (facade -> department -> here).
	 *  Handlers stay registered on their lanes after firing, exactly like
	 *  the reference closures (a later lerp completion re-fires them). */
	public dispatchPhysicsTargetReached(lane: number): void {
		const id = this.physHandler[lane - this.range.start];
		if (id > 0) {
			this._physicsHandlers[id - 1](lane);
		}
	}

	//Shared lane helpers (verbatim reference ports)
	//-----------------------------------------------------------------------------------------------

	/** deactivateAllBehaviours(true): behaviours off, position stays.
	 *  Breathing state and disableTimelineX survive (animatedProps/position
	 *  config, not behaviours). */
	public deactivateAll(lane: number): void {
		const core = this.core;
		core.behaviourMask[lane] =
			(core.behaviourMask[lane] & (BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X)) | BEHAVIOUR_PROGRESS_PARALLAX;
	}

	/** SpacesParticleProcessor._reset, lane-for-line. */
	public resetLane(lane: number): void {
		const core = this.core;

		//orbitPath.expandMuliplier = 1 (Inertia TARGET write):
		core.orbitPath.expandTarget[lane] = 1;
		//onTargetReached = null:
		this.physHandler[lane - this.range.start] = 0;
		//counter.onTrigger = null / active = false — covered by the phase
		//transition's group cancel (see GROUP_SPACES).
		//setVectorStyleMovement(false) — the reference still rolls fresh
		//randomised rates on the way off:
		writeVectorConfig(core, lane, VEC_X, false);
		writeVectorConfig(core, lane, VEC_Y, false);
	}

	/** _spacesRepellStart: PUSH at radius 100 off spacesMouse (the facade's
	 *  spaces repell centre). */
	public spacesRepellStart(lane: number): void {
		const core = this.core;
		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 100;
		core.repellPower[lane] = 100 + Math.random() * 20;
	}

	/** setEase for x/y — clears any pending ease-lerp (Inertia.setEase ->
	 *  resetEaseLerp). */
	public setEaseXY(lane: number, x: number, y: number): void {
		const core = this.core;
		core.easeX[lane] = x;
		core.easeY[lane] = y;
		core.easeLerpFramesX[lane] = 0;
		core.easeLerpFramesY[lane] = 0;
	}

	/** ParticlePhysics.lerpToPosition (rel space; ease -1 = the random 1|0
	 *  roll). lerpStart reads the CURRENT rel lanes like position.x/y. */
	public lerpToPosition(lane: number, x: number, y: number, timeMs: number, ease: number = -1): void {
		const core = this.core;

		core.lerpFrames[lane] = 0;
		core.lerpTargetFrames[lane] = timeMs / 16;

		core.lerpStartX[lane] = core.relX[lane];
		core.lerpStartY[lane] = core.relY[lane];

		core.lerpEndX[lane] = x;
		core.lerpEndY[lane] = y;

		core.physFlags[lane] |= PHYS_LERPING;

		core.lerpEase[lane] = ease === -1 ? (Math.random() < 0.5 ? 1 : 0) : ease;
	}

	/** HomeParticle.hide(): the free-particle sweep (the _ta/_ts show()
	 *  restore memory has no live spaces caller — not ported). */
	public hideLane(lane: number): void {
		const core = this.core;

		core.behaviourMask[lane] &= ~BEHAVIOUR_PATH_ORBIT;

		core.tAlpha[lane] = 0;
		core.tScale[lane] = 0;

		core.easeScale[lane] = 30;
		core.easeAlpha[lane] = 30;
		this.setEaseXY(lane, 1, 1);
	}

	/** SpacesDepartment._getFrameSizes — the box frame in rel units. Pass
	 *  `out` to reuse an object (the reference quick-wins pattern on the
	 *  sine hot path). */
	public getFrameSizes(out?: FrameSizes): FrameSizes {
		const sizes = this.deps.getSizes();
		const box = sizes.zonePositions.box;

		//ParticleCords.xPxToRel(box.widthPx) = widthPx / container.w:
		const w = box.widthPx / sizes.virtualWidth;
		const frameX1 = -1 * (w / 2);
		const frameX2 = w / 2;
		const frameWidth = frameX2 - frameX1;
		const frameXC = frameX1 + frameWidth / 2;

		const frameY1 = box.y;
		const frameY2 = box.y + box.h;
		const frameHeight = frameY2 - frameY1;
		const frameYC = frameY1 + frameHeight / 2;

		const result = out || ({} as FrameSizes);

		result.x1 = frameX1;
		result.x2 = frameX2;
		result.width = frameWidth;
		result.xc = frameXC;
		result.y1 = frameY1;
		result.y2 = frameY2;
		result.height = frameHeight;
		result.yc = frameYC;

		return result;
	}
}
